// World-door screen (DOR-1/3/4, M41). Using a `world_door` opens this menu:
//  • unbound → create a new child world (any type + footprint) or link to an existing
//    world in the save; binding is atomic (both edges or neither, handled in world/doors).
//  • bound + resolvable → "Enter" (a world-session-swap traversal) + "Resize" (grow always;
//    shrink refused while builds lie outside the new bounds, the blockers listed).
//  • bound + the target was deleted → inert with a clear message (E-34).
// Presentation only — all graph/traversal/bounds logic lives in world/doors + boot.
import { h } from '../h';
import type { Game } from '../../boot';
import type { WorldMeta, WorldType } from '../../storage/saves';
import type { WorldDoorData } from '../../../sim/game/blockentity';
import type { ChildOpts, ResizeResult } from '../../world/doors';
import { DOOR } from '../../../sim/config';

export interface DoorScreenApi {
  /** The world this door lives in (for the escape-hatch / "you are here" label). */
  meta: WorldMeta;
  /** The door's current edge data (live). */
  door(): WorldDoorData;
  /** Resolve the door's bound target world, or undefined (unbound / deleted = E-34). */
  resolveTarget(): Promise<WorldMeta | undefined>;
  /** Every world in the save (for the link-to-existing picker). */
  listWorlds(): Promise<WorldMeta[]>;
  /** Create a bounded child world and bind this door to it (DOR-1/3). */
  createChild(opts: ChildOpts): Promise<void>;
  /** Link this door to an already-existing world (DOR-1). */
  bindExisting(targetWorldId: string): Promise<{ ok: boolean; error?: string }>;
  /** Traverse the door (world-session swap; navigates away, DOR-2). */
  enter(): Promise<void>;
  /** Resize the bound target's footprint (DOR-4); refuse-shrink returns blockers. */
  resize(size: number): Promise<ResizeResult>;
}

const WORLD_TYPES: { value: WorldType; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'flat', label: 'Flat' },
  { value: 'atlas', label: 'Atlas (simulated city)' },
  { value: 'earth', label: 'Earth (real place)' },
];

export function openDoorScreen(game: Game, api: DoorScreenApi): void {
  game.uiOpen = true;
  document.exitPointerLock();

  const overlay = h('div', { class: 'bb-window-overlay' });
  const panel = h('div', { class: 'bb-window bb-doorscreen', 'data-testid': 'door-screen' });
  overlay.append(panel);
  document.body.append(overlay);

  const close = (): void => { overlay.remove(); game.uiOpen = false; };
  overlay.onclick = (e) => { if (e.target === overlay) close(); };

  const body = h('div', { class: 'bb-door-body' });
  const status = h('div', { class: 'bb-script-status bb-script-ok', 'data-testid': 'door-status' }, 'Loading…');

  const closeBtn = h('button', { class: 'bb-btn', 'data-testid': 'door-close' }, 'Close') as HTMLButtonElement;
  closeBtn.onclick = close;

  panel.append(
    h('div', { class: 'bb-window-title' }, '🚪 World Door'),
    status,
    body,
    h('div', { class: 'bb-row' }, closeBtn),
  );

  // ── Unbound: create a new child, or link to an existing world ──
  const renderUnbound = async (): Promise<void> => {
    status.textContent = 'This door is not linked yet — create a world or pick one to link.';
    body.innerHTML = '';

    const nameIn = h('input', { type: 'text', value: 'New World', 'data-testid': 'door-name' }) as HTMLInputElement;
    const typeSel = h('select', { 'data-testid': 'door-type' }) as HTMLSelectElement;
    for (const t of WORLD_TYPES) typeSel.append(h('option', { value: t.value }, t.label));
    const sizeIn = h('input', { type: 'number', min: String(DOOR.MIN_SIZE), max: String(DOOR.MAX_SIZE), value: String(DOOR.CHILD_DEFAULT_SIZE), 'data-testid': 'door-size' }) as HTMLInputElement;

    const createBtn = h('button', { class: 'bb-btn bb-primary', 'data-testid': 'door-create' }, 'Create & link') as HTMLButtonElement;
    createBtn.onclick = async () => {
      createBtn.disabled = true;
      status.textContent = 'Creating world…';
      await api.createChild({ worldType: typeSel.value as WorldType, size: Number(sizeIn.value), name: nameIn.value });
      await renderBound();
    };

    body.append(
      h('div', { class: 'bb-door-section' },
        h('div', { class: 'bb-door-h' }, 'Create a new world'),
        h('div', { class: 'bb-row' }, h('span', { class: 'bb-muted' }, 'Name'), nameIn),
        h('div', { class: 'bb-row' }, h('span', { class: 'bb-muted' }, 'Type'), typeSel),
        h('div', { class: 'bb-row' }, h('span', { class: 'bb-muted' }, 'Size (blocks²)'), sizeIn),
        h('div', { class: 'bb-row' }, createBtn),
      ),
    );

    // Link to an existing world (any other world in the save).
    const worlds = (await api.listWorlds()).filter((w) => w.id !== api.meta.id);
    if (worlds.length) {
      const pick = h('select', { 'data-testid': 'door-link-pick' }) as HTMLSelectElement;
      for (const w of worlds) pick.append(h('option', { value: w.id }, `${w.name} (${w.worldType})`));
      const linkBtn = h('button', { class: 'bb-btn', 'data-testid': 'door-link' }, 'Link') as HTMLButtonElement;
      linkBtn.onclick = async () => {
        linkBtn.disabled = true;
        const r = await api.bindExisting(pick.value);
        if (r.ok) await renderBound();
        else { status.textContent = `Could not link: ${r.error ?? 'unknown'}`; linkBtn.disabled = false; }
      };
      body.append(
        h('div', { class: 'bb-door-section' },
          h('div', { class: 'bb-door-h' }, 'Or link to an existing world'),
          h('div', { class: 'bb-row' }, pick, linkBtn),
        ),
      );
    }
  };

  // ── Bound: enter + resize (or an inert message if the target was deleted, E-34) ──
  const renderBound = async (): Promise<void> => {
    body.innerHTML = '';
    const target = await api.resolveTarget();
    if (!target) {
      status.textContent = '⚠ This door leads nowhere — the linked world was deleted.';
      status.className = 'bb-script-status bb-script-err';
      body.append(h('div', { class: 'bb-muted', 'data-testid': 'door-inert' }, 'Break the door to reclaim it, or link it again.'));
      return;
    }
    status.textContent = `Linked to “${target.name}”.`;
    status.className = 'bb-script-status bb-script-ok';

    const enterBtn = h('button', { class: 'bb-btn bb-primary', 'data-testid': 'door-enter' }, `Enter ${target.name} ▸`) as HTMLButtonElement;
    enterBtn.onclick = async () => { enterBtn.disabled = true; status.textContent = 'Entering…'; await api.enter(); };

    body.append(h('div', { class: 'bb-door-section' }, h('div', { class: 'bb-row' }, enterBtn)));

    // Resize (only for a bounded child world, DOR-4).
    if (target.bounds) {
      const sizeIn = h('input', { type: 'number', min: String(DOOR.MIN_SIZE), max: String(DOOR.MAX_SIZE), value: String(target.bounds.size), 'data-testid': 'door-resize-size' }) as HTMLInputElement;
      const applyBtn = h('button', { class: 'bb-btn', 'data-testid': 'door-resize' }, 'Resize') as HTMLButtonElement;
      const note = h('div', { class: 'bb-muted', 'data-testid': 'door-resize-note' }, `Current: ${target.bounds.size} blocks². Growing is always safe; shrinking is refused while builds lie outside.`);
      applyBtn.onclick = async () => {
        applyBtn.disabled = true;
        const r = await api.resize(Number(sizeIn.value));
        if (r.ok) note.textContent = `Resized to ${r.size} blocks².`;
        else {
          const listed = (r.blockers ?? []).slice(0, 8).map((b) => `${b.kind}@(${b.x},${b.z})`).join(', ');
          note.textContent = `Shrink refused — ${(r.blockers ?? []).length} build(s) outside the new bounds: ${listed}${(r.blockers ?? []).length > 8 ? '…' : ''}`;
        }
        applyBtn.disabled = false;
      };
      body.append(
        h('div', { class: 'bb-door-section' },
          h('div', { class: 'bb-door-h' }, 'Resize world'),
          h('div', { class: 'bb-row' }, h('span', { class: 'bb-muted' }, 'Size (blocks²)'), sizeIn, applyBtn),
          note,
        ),
      );
    }
  };

  // Initial render: bound doors go straight to the enter/resize view.
  const door = api.door();
  void (door.targetWorldId ? renderBound() : renderUnbound());
}
